import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, Calendar, ArrowRightLeft, DollarSign, Users, Filter, X,
  CalendarDays, CalendarPlus, FileText, Printer, Ban, Settings
} from 'lucide-react';
import AfterschoolEnrollModal from './modals/AfterschoolEnrollModal';
import CapSettingsModal from './modals/CapSettingsModal';
import AfterschoolDayChargesModal from './modals/AfterschoolDayChargesModal';
import AfterschoolTransitionModal from './modals/AfterschoolTransitionModal';
import ConfirmDialog from './modals/ConfirmDialog';
import { Gate, useAccessStudio } from './AccessStudio';

// Tab visibility -> required view permission.
const AFTERSCHOOL_TAB_PERMISSIONS = {
  enrollments: 'afterschool.enrollments.view',
  daily: 'afterschool.daily.view',
  billing: 'afterschool.billing.view',
  transitions: 'afterschool.transitions.view',
  report: 'afterschool.reports.view',
};

const TAB_ORDER = ['enrollments', 'daily', 'billing', 'transitions', 'report'];

const TAB_LABELS = {
  enrollments: '📋 Enrollments',
  daily: '📅 Daily Charges',
  billing: '💵 Billing',
  transitions: '🔄 Package Changes',
  report: '📊 Report',
};

const money = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0.00';
  return Number(value).toFixed(2);
};

const isDaily = (enrollment) => (enrollment.BillingType || '').toLowerCase() === 'daily';

const Afterschool = ({ apiCall, selectedYear, hasRole, showSuccess, showError }) => {
  const [packages, setPackages] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [transitions, setTransitions] = useState([]);
  const [students, setStudents] = useState([]);
  const [report, setReport] = useState({ families: [], months: [] });
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('enrollments');
  const { userHasPermission } = useAccessStudio();

  // If the user can't see the current tab, land them on the first tab they can.
  useEffect(() => {
    const canSee = (tab) => {
      const [m, f, a] = AFTERSCHOOL_TAB_PERMISSIONS[tab].split('.');
      return userHasPermission(m, f, a);
    };
    if (!canSee(activeTab)) {
      const first = TAB_ORDER.find(canSee);
      if (first) setActiveTab(first);
    }
  }, [userHasPermission, activeTab]);

  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showCapSettings, setShowCapSettings] = useState(false);
  const [showDayChargesModal, setShowDayChargesModal] = useState(false);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);

  // One dialog descriptor drives every confirm/prompt on the page.
  const [dialog, setDialog] = useState(null);
  const [dialogBusy, setDialogBusy] = useState(false);
  const closeDialog = () => { setDialog(null); setDialogBusy(false); };

  const [filters, setFilters] = useState({
    search: '',
    status: 'Active',
    package: 'all',
    billingType: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [packagesData, enrollmentsData, studentsData] = await Promise.all([
        apiCall('/afterschool/packages'),
        apiCall(`/afterschool/enrollments?academicYear=${selectedYear}`),
        apiCall(`/students?academicYear=${selectedYear}`),
      ]);
      setPackages(packagesData);
      setEnrollments(enrollmentsData);
      setStudents(studentsData);

      // Transitions are permission-gated separately; a denial here must not
      // blank out the whole page.
      try {
        const transitionsData = await apiCall(`/afterschool/transitions?academicYear=${selectedYear}`);
        setTransitions(transitionsData);
      } catch {
        setTransitions([]);
      }
    } catch (error) {
      showError('Error loading afterschool data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, selectedYear, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadReport = useCallback(async () => {
    try {
      const data = await apiCall(`/afterschool/report?academicYear=${selectedYear}`);
      setReport(data);
    } catch (error) {
      showError('Error loading report: ' + error.message);
    }
  }, [apiCall, selectedYear, showError]);

  useEffect(() => {
    if (activeTab === 'report') loadReport();
  }, [activeTab, loadReport]);

  // ---------- derived ----------

  const dailyEnrollments = useMemo(() => enrollments.filter(isDaily), [enrollments]);

  const filteredEnrollments = useMemo(() => {
    return enrollments.filter((e) => {
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const match =
          e.FirstName?.toLowerCase().includes(q) ||
          e.LastName?.toLowerCase().includes(q) ||
          e.FatherName?.toLowerCase().includes(q) ||
          e.MotherName?.toLowerCase().includes(q) ||
          e.StudentID?.toLowerCase().includes(q) ||
          e.FamilyID?.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (filters.status !== 'all' && e.Status !== filters.status) return false;
      if (filters.package !== 'all' && e.PackageID !== parseInt(filters.package, 10)) return false;
      if (filters.billingType !== 'all' && (e.BillingType || '').toLowerCase() !== filters.billingType) return false;
      return true;
    });
  }, [enrollments, filters]);

  const stats = useMemo(() => {
    const active = enrollments.filter((e) => e.Status === 'Active');
    const monthly = active.filter((e) => !isDaily(e));
    return {
      activeCount: active.length,
      dailyCount: active.filter(isDaily).length,
      families: new Set(active.map((e) => e.FamilyID)).size,
      // Recurring revenue is a monthly-only concept — daily enrollments bill
      // ad hoc, so folding them in would overstate it.
      monthlyRevenue: monthly.reduce((sum, e) => sum + (Number(e.FinalCost) || 0), 0),
      outstanding: enrollments.reduce((sum, e) => sum + (Number(e.TrueBalance) || 0), 0),
    };
  }, [enrollments]);

  const activeFilterCount = useMemo(
    () =>
      (filters.search ? 1 : 0) +
      (filters.status !== 'Active' ? 1 : 0) +
      (filters.package !== 'all' ? 1 : 0) +
      (filters.billingType !== 'all' ? 1 : 0),
    [filters]
  );

  const clearFilters = () =>
    setFilters({ search: '', status: 'Active', package: 'all', billingType: 'all' });

  const reportTotals = useMemo(() => {
    const f = report.families || [];
    return {
      families: f.length,
      charged: f.reduce((s, r) => s + (Number(r.TotalCharged) || 0), 0),
      discount: f.reduce((s, r) => s + (Number(r.TotalDiscount) || 0), 0),
      paid: f.reduce((s, r) => s + (Number(r.TotalPaid) || 0), 0),
      balance: f.reduce((s, r) => s + (Number(r.Balance) || 0), 0),
      daily: f.reduce((s, r) => s + (Number(r.DailyCharged) || 0), 0),
      monthly: f.reduce((s, r) => s + (Number(r.MonthlyCharged) || 0), 0),
    };
  }, [report]);

  // ---------- actions ----------

  const askCancelEnrollment = (enrollment) => {
    setDialog({
      kind: 'cancelEnrollment',
      enrollment,
      tone: 'danger',
      title: 'Cancel enrollment',
      message: `Cancel the afterschool enrollment for ${enrollment.FirstName} ${enrollment.LastName}?`,
      details: 'Charges already billed stay on the family’s balance. This does not refund anything.',
      confirmLabel: 'Cancel enrollment',
      cancelLabel: 'Keep it',
      requireInput: true,
      inputLabel: 'Cancellation reason',
      inputPlaceholder: 'e.g. Left the program',
    });
  };

  const askCancelTransition = (transition) => {
    setDialog({
      kind: 'cancelTransition',
      transition,
      tone: 'warning',
      title: 'Cancel package change',
      message: `Drop the scheduled change to ${transition.NewPackageName} for ${transition.FirstName} ${transition.LastName}?`,
      confirmLabel: 'Cancel change',
      cancelLabel: 'Keep it',
    });
  };

  const [billingMonth, setBillingMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const monthLabel = (value) => {
    const [y, m] = (value || '').split('-');
    if (!y || !m) return '';
    return new Date(Number(y), Number(m) - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const askProcessCharges = () => {
    const label = monthLabel(billingMonth);
    const eligible = stats.activeCount - stats.dailyCount;
    setDialog({
      kind: 'processCharges',
      tone: 'info',
      title: `Generate ${label} charges`,
      message: `One charge will be created for each active monthly enrollment (${eligible} eligible).`,
      details: 'Enrollments already billed for this month are skipped, so running it twice is safe. Daily enrollments are not affected.',
      confirmLabel: 'Generate charges',
    });
  };

  const runDialogAction = async (inputValue) => {
    if (!dialog) return;
    setDialogBusy(true);
    try {
      if (dialog.kind === 'cancelEnrollment') {
        await apiCall(`/afterschool/enrollments/${dialog.enrollment.EnrollmentID}/cancel`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cancellationReason: inputValue,
            effectiveDate: new Date().toISOString().split('T')[0],
          }),
        });
        showSuccess('Enrollment cancelled');
      } else if (dialog.kind === 'cancelTransition') {
        await apiCall(`/afterschool/transitions/${dialog.transition.TransitionID}`, { method: 'DELETE' });
        showSuccess('Package change cancelled');
      } else if (dialog.kind === 'processCharges') {
        const [year, month] = billingMonth.split('-');
        // Bill on the 1st: the charge belongs to the month, not to today.
        const result = await apiCall('/afterschool/process-monthly-charges', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            processDate: `${year}-${month}-01`,
            academicYear: selectedYear,
          }),
        });
        const generated = result.ChargesGenerated ?? 0;
        showSuccess(
          generated > 0
            ? `${monthLabel(billingMonth)}: ${generated} charge${generated !== 1 ? 's' : ''} generated`
            : `${monthLabel(billingMonth)}: nothing to do — every active enrollment is already billed`
        );
      }
      closeDialog();
      loadData();
    } catch (error) {
      showError('Error: ' + error.message);
      setDialogBusy(false);
    }
  };

  // ---------- report output ----------

  const [printScope, setPrintScope] = useState('both'); // both | months | families

  const PRINT_CSS = `
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:Arial,sans-serif; padding:20px; background:#fff; }
    .header { text-align:center; border-bottom:3px solid #1e40af; padding-bottom:15px; margin-bottom:20px; }
    .header h1 { font-size:26px; color:#1e3a8a; margin-bottom:4px; }
    .header .subtitle { font-size:15px; color:#4b5563; font-weight:600; }
    .header .date-info { font-size:11px; color:#6b7280; margin-top:6px; }
    h2 { font-size:15px; color:#1e3a8a; margin:20px 0 6px; }
    table { width:100%; border-collapse:collapse; margin-top:8px; }
    thead { background:#f3f4f6; }
    th { padding:8px 6px; text-align:left; font-size:10px; font-weight:700; color:#374151;
         text-transform:uppercase; border:1px solid #d1d5db; }
    td { padding:6px; font-size:11px; border:1px solid #e5e7eb; color:#1f2937; }
    tbody tr:nth-child(even) { background:#f9fafb; }
    th.num, td.num { text-align:right; }
    tfoot { background:#f9fafb; border-top:2px solid #1e40af; }
    tfoot td { font-weight:700; font-size:12px; }
    .footer { margin-top:30px; padding-top:12px; border-top:2px solid #e5e7eb;
              text-align:center; font-size:10px; color:#6b7280; }
    .footer .school-name { font-weight:700; color:#1e40af; margin-bottom:3px; }
    @media print { body { padding:12px; } thead { display:table-header-group; } tr { page-break-inside:avoid; } }
  `;

  const buildPrintHtml = (scope) => {
    const generated = new Date().toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    const title =
      scope === 'months' ? 'Afterschool Report — Month by Month'
        : scope === 'families' ? 'Afterschool Report — By Family'
        : 'Afterschool Report';

    const monthsTable = `
      <h2>Month by Month</h2>
      <table>
        <thead><tr>
          <th>Month</th><th class="num">Charges</th><th class="num">Students</th>
          <th class="num">Gross</th><th class="num">Discounts</th><th class="num">Net</th>
          <th class="num">Monthly</th><th class="num">Daily</th>
          <th class="num">Collected</th><th class="num">Outstanding</th>
        </tr></thead>
        <tbody>
          ${(report.months || []).map((m) => `
            <tr>
              <td>${m.MonthName} ${m.ChargeYear}</td>
              <td class="num">${m.Charges}</td>
              <td class="num">${m.Students}</td>
              <td class="num">$${money(m.Gross)}</td>
              <td class="num">-$${money(m.Discounts)}</td>
              <td class="num">$${money(m.Net)}</td>
              <td class="num">$${money(m.MonthlyNet)}</td>
              <td class="num">$${money(m.DailyNet)}</td>
              <td class="num">$${money(m.Collected)}</td>
              <td class="num">$${money(m.Outstanding)}</td>
            </tr>`).join('')}
          ${(report.months || []).length === 0 ? '<tr><td colspan="10">No charges recorded</td></tr>' : ''}
        </tbody>
      </table>`;

    const familiesTable = `
      <h2>By Family</h2>
      <table>
        <thead><tr>
          <th>Family ID</th><th>Family</th><th class="num">Students</th>
          <th class="num">Monthly</th><th class="num">Daily</th>
          <th class="num">Charged</th><th class="num">Paid</th><th class="num">Balance</th>
        </tr></thead>
        <tbody>
          ${(report.families || []).map((r) => `
            <tr>
              <td>${r.FamilyID}</td>
              <td>${r.FamilyName || ''}</td>
              <td class="num">${r.Students}</td>
              <td class="num">$${money(r.MonthlyCharged)}</td>
              <td class="num">$${money(r.DailyCharged)}</td>
              <td class="num">$${money(r.TotalCharged)}</td>
              <td class="num">$${money(r.TotalPaid)}</td>
              <td class="num">$${money(r.Balance)}</td>
            </tr>`).join('')}
          ${(report.families || []).length === 0 ? '<tr><td colspan="8">No families with charges</td></tr>' : ''}
        </tbody>
        <tfoot><tr>
          <td colspan="3">TOTAL</td>
          <td class="num">$${money(reportTotals.monthly)}</td>
          <td class="num">$${money(reportTotals.daily)}</td>
          <td class="num">$${money(reportTotals.charged)}</td>
          <td class="num">$${money(reportTotals.paid)}</td>
          <td class="num">$${money(reportTotals.balance)}</td>
        </tr></tfoot>
      </table>`;

    return `<!DOCTYPE html><html><head><title>${title} - ${selectedYear}</title>
      <style>${PRINT_CSS}</style></head><body>
      <div class="header">
        <h1>Collège des Apôtres</h1>
        <div class="subtitle">${title}</div>
        <div class="date-info">Academic Year: ${selectedYear} | Generated: ${generated}</div>
      </div>
      ${scope !== 'families' ? monthsTable : ''}
      ${scope !== 'months' ? familiesTable : ''}
      <div class="footer">
        <div class="school-name">Collège des Apôtres</div>
        <div>Jounieh, Lebanon | Tel: +961 5 924 845</div>
        <div style="margin-top:4px">This report was generated electronically and is valid without signature</div>
      </div></body></html>`;
  };

  const handlePrintReport = () => {
    const win = window.open('', '', 'width=980,height=760');
    win.document.write(buildPrintHtml(printScope));
    win.document.close();
    win.print();
  };

  const handleExportCsv = () => {
    let rows;
    if (printScope === 'months') {
      rows = [
        ['Month', 'Charges', 'Students', 'Gross', 'Discounts', 'Net', 'Monthly', 'Daily', 'Collected', 'Outstanding'],
        ...(report.months || []).map((m) => [
          `${m.MonthName} ${m.ChargeYear}`, m.Charges, m.Students,
          money(m.Gross), money(m.Discounts), money(m.Net),
          money(m.MonthlyNet), money(m.DailyNet),
          money(m.Collected), money(m.Outstanding),
        ]),
      ];
    } else {
      rows = [
        ['Family ID', 'Family Name', 'Students', 'Monthly', 'Daily', 'Charged', 'Discount', 'Paid', 'Balance'],
        ...(report.families || []).map((r) => [
          r.FamilyID,
          `"${(r.FamilyName || '').replace(/"/g, '""')}"`,
          r.Students, money(r.MonthlyCharged), money(r.DailyCharged),
          money(r.TotalCharged), money(r.TotalDiscount), money(r.TotalPaid), money(r.Balance),
        ]),
        ['', 'TOTAL', '', money(reportTotals.monthly), money(reportTotals.daily),
          money(reportTotals.charged), money(reportTotals.discount),
          money(reportTotals.paid), money(reportTotals.balance)],
      ];
    }
    const csv = rows.map((r) => r.join(',')).join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `afterschool-${printScope === 'months' ? 'monthly' : 'families'}-${selectedYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading afterschool data...</div>
      </div>
    );
  }

  const statusBadge = (status) =>
    status === 'Active' ? 'bg-green-100 text-green-800'
      : status === 'Cancelled' ? 'bg-gray-100 text-gray-700'
      : 'bg-yellow-100 text-yellow-800';

  const iconBtn = 'p-1.5 rounded hover:bg-gray-100 transition-colors';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Afterschool Management</h1>
        <p className="text-gray-600 mt-1">Academic Year: {selectedYear}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeCount}</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Families</p>
              <p className="text-2xl font-bold text-gray-900">{stats.families}</p>
            </div>
            <Users className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Daily Billing</p>
              <p className="text-2xl font-bold text-gray-900">{stats.dailyCount}</p>
            </div>
            <CalendarDays className="w-8 h-8 text-amber-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Monthly Recurring</p>
              <p className="text-2xl font-bold text-green-600">${money(stats.monthlyRevenue)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Outstanding</p>
              <p className={`text-2xl font-bold ${stats.outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                ${money(stats.outstanding)}
              </p>
            </div>
            <DollarSign className={`w-8 h-8 ${stats.outstanding > 0 ? 'text-red-500' : 'text-green-500'}`} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {TAB_ORDER.map((tab) => (
              <Gate key={tab} permission={AFTERSCHOOL_TAB_PERMISSIONS[tab]} label={`${tab} tab`}>
                <button
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 border-b-2 font-medium text-sm ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {TAB_LABELS[tab]}
                  {tab === 'transitions' && transitions.length > 0 && ` (${transitions.length})`}
                </button>
              </Gate>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* ---------------- ENROLLMENTS ---------------- */}
          {activeTab === 'enrollments' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Enrollments</h2>
                <div className="flex items-center gap-2">
                  <Gate permission="afterschool.catalog.manage" label="CAP Settings">
                    <button
                      onClick={() => setShowCapSettings(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      title="Configure packages, add-ons and class eligibility for this year"
                    >
                      <Settings className="w-4 h-4" />
                      CAP Settings
                    </button>
                  </Gate>
                  <Gate permission="afterschool.enrollments.create" label="New Enrollment">
                    <button
                      onClick={() => setShowEnrollModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      New Enrollment
                    </button>
                  </Gate>
                </div>
              </div>

              {/* Filters */}
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                      showFilters ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-300'
                    } hover:bg-blue-50`}
                  >
                    <Filter className="h-4 w-4" />
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                    >
                      <X className="h-4 w-4" />
                      Reset
                    </button>
                  )}
                  <div className="text-sm text-gray-600 ml-auto">
                    Showing {filteredEnrollments.length} of {enrollments.length}
                  </div>
                </div>

                {showFilters && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg border">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Search</label>
                      <input
                        type="text"
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        placeholder="Name, ID, Family..."
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
                      <select
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      >
                        <option value="Active">Active</option>
                        <option value="Cancelled">Cancelled</option>
                        <option value="all">All</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Package</label>
                      <select
                        value={filters.package}
                        onChange={(e) => setFilters({ ...filters, package: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      >
                        <option value="all">All Packages</option>
                        {packages.map((p) => (
                          <option key={p.PackageID} value={p.PackageID}>
                            {p.PackageName} {p.TimeSlot ? `(${p.TimeSlot})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Billing</label>
                      <select
                        value={filters.billingType}
                        onChange={(e) => setFilters({ ...filters, billingType: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      >
                        <option value="all">All</option>
                        <option value="monthly">Monthly</option>
                        <option value="daily">Daily</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Compact: student/class, family, package+billing, rate, charged,
                  balance and actions all fit without horizontal scrolling. */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Family</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Charged</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEnrollments.map((e) => (
                      <tr key={e.EnrollmentID} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {e.FirstName} {e.LastName}
                            {e.Status !== 'Active' && (
                              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${statusBadge(e.Status)}`}>
                                {e.Status}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">{e.Grade || '—'} · {e.StudentID}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-gray-800 truncate max-w-[11rem]">
                            {e.FatherName || e.MotherName || '—'}
                          </div>
                          <div className="text-xs text-gray-500">{e.FamilyID}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-gray-800 truncate max-w-[10rem]">{e.PackageName}</span>
                            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                              isDaily(e) ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {isDaily(e) ? 'Daily' : 'Monthly'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">{e.TimeSlot || e.PackageTimeSlot || ''}</div>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <div className="font-semibold text-blue-600">${money(e.FinalCost)}</div>
                          {Number(e.SiblingDiscount) > 0 && (
                            <div className="text-xs text-green-600">-${money(e.SiblingDiscount)}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <div>${money(e.ChargedToDate)}</div>
                          <div className="text-xs text-gray-500">
                            {e.ChargeCount || 0} charge{(e.ChargeCount || 0) !== 1 ? 's' : ''}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <span className={`font-semibold ${
                            Number(e.TrueBalance) > 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            ${money(e.TrueBalance)}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-0.5">
                            {isDaily(e) && e.Status === 'Active' && (
                              <Gate permission="afterschool.daily.manage" label="Add Days">
                                <button
                                  onClick={() => { setSelectedEnrollment(e); setShowDayChargesModal(true); }}
                                  className={`${iconBtn} text-amber-600 hover:text-amber-800`}
                                  title="Add / view days"
                                >
                                  <CalendarPlus className="w-4 h-4" />
                                </button>
                              </Gate>
                            )}
                            {!isDaily(e) && e.Status === 'Active' && (
                              <Gate permission="afterschool.transitions.manage" label="Change Package">
                                <button
                                  onClick={() => { setSelectedEnrollment(e); setShowTransitionModal(true); }}
                                  className={`${iconBtn} text-purple-600 hover:text-purple-800`}
                                  title="Change package"
                                >
                                  <ArrowRightLeft className="w-4 h-4" />
                                </button>
                              </Gate>
                            )}
                            {e.Status === 'Active' && (
                              <Gate permission="afterschool.enrollments.cancel" label="Cancel Enrollment">
                                <button
                                  onClick={() => askCancelEnrollment(e)}
                                  className={`${iconBtn} text-red-600 hover:text-red-800`}
                                  title="Cancel enrollment"
                                >
                                  <Ban className="w-4 h-4" />
                                </button>
                              </Gate>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredEnrollments.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  {enrollments.length === 0
                    ? `No afterschool enrollments for ${selectedYear}`
                    : 'No enrollments match your filters'}
                </div>
              )}
            </div>
          )}

          {/* ---------------- DAILY ---------------- */}
          {activeTab === 'daily' && (
            <div>
              <h2 className="text-xl font-semibold mb-2">Daily Charges</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-sm text-amber-900">
                Daily enrollments are not billed automatically. Each time a student attends,
                log the day(s) here — a student can be charged as many times as they attend
                over the year. Charges post to the family balance immediately.
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Family</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Day Rate</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Times</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Charged</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dailyEnrollments.map((e) => (
                      <tr key={e.EnrollmentID} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {e.FirstName} {e.LastName}
                            {e.Status !== 'Active' && (
                              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${statusBadge(e.Status)}`}>
                                {e.Status}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">{e.Grade || '—'}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-gray-800 truncate max-w-[11rem]">
                            {e.FatherName || e.MotherName || '—'}
                          </div>
                          <div className="text-xs text-gray-500">{e.FamilyID}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-800">{e.PackageName}</td>
                        <td className="px-3 py-2 text-right font-semibold text-blue-600">
                          ${money(e.DailyPrice ?? e.FinalCost)}
                        </td>
                        <td className="px-3 py-2 text-right">{e.ChargeCount || 0}</td>
                        <td className="px-3 py-2 text-right">${money(e.ChargedToDate)}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={`font-semibold ${
                            Number(e.TrueBalance) > 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            ${money(e.TrueBalance)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Gate permission="afterschool.daily.manage" label="Manage Days">
                            <button
                              onClick={() => { setSelectedEnrollment(e); setShowDayChargesModal(true); }}
                              className="px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-xs font-semibold whitespace-nowrap"
                            >
                              {e.Status === 'Active' ? 'Add / View Days' : 'View Days'}
                            </button>
                          </Gate>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {dailyEnrollments.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No daily enrollments for {selectedYear}
                </div>
              )}
            </div>
          )}

          {/* ---------------- BILLING ---------------- */}
          {activeTab === 'billing' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Monthly Billing</h2>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">How billing works</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  <li>Pick a month and run it — one charge per active <strong>monthly</strong> enrollment.</li>
                  <li>Enrollments already billed for that month are skipped, so re-running is safe.</li>
                  <li>Daily enrollments are not touched here — log those on the Daily Charges tab.</li>
                  <li>Families pay through the Payments module; balances update automatically.</li>
                </ol>
              </div>

              <div className="bg-white border rounded-lg p-6 mb-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Billing month</label>
                    <input
                      type="month"
                      value={billingMonth}
                      onChange={(ev) => setBillingMonth(ev.target.value)}
                      className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <Gate permission="afterschool.billing.process" label="Process Monthly Charges">
                    <button
                      onClick={askProcessCharges}
                      disabled={!billingMonth}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      <DollarSign className="w-4 h-4" />
                      Process Monthly Charges
                    </button>
                  </Gate>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="text-sm text-gray-600 mb-1">Active Monthly Enrollments</div>
                  <div className="text-3xl font-bold text-gray-900">
                    {stats.activeCount - stats.dailyCount}
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="text-sm text-gray-600 mb-1">Expected Per Month</div>
                  <div className="text-3xl font-bold text-green-600">${money(stats.monthlyRevenue)}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="text-sm text-gray-600 mb-1">Outstanding</div>
                  <div className="text-3xl font-bold text-red-600">${money(stats.outstanding)}</div>
                </div>
              </div>
            </div>
          )}

          {/* ---------------- TRANSITIONS ---------------- */}
          {activeTab === 'transitions' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Scheduled Package Changes</h2>

              {transitions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ArrowRightLeft className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-semibold">No pending package changes</p>
                  <p className="text-sm">
                    Schedule one from the Enrollments tab using the package-change action.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">New Rate</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Effective</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Billed Ahead</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transitions.map((t) => (
                        <tr key={t.TransitionID} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <div className="font-medium">{t.FirstName} {t.LastName}</div>
                            <div className="text-xs text-gray-500">{t.FamilyID}</div>
                          </td>
                          <td className="px-3 py-2">{t.CurrentPackageName}</td>
                          <td className="px-3 py-2 font-medium text-purple-700">{t.NewPackageName}</td>
                          <td className="px-3 py-2 text-right font-semibold text-blue-600">
                            ${money(t.NewFinalCost)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {t.EffectiveDate ? new Date(t.EffectiveDate).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-3 py-2">
                            {t.BilledInAdvance
                              ? <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">Yes</span>
                              : <span className="text-gray-400">No</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Gate permission="afterschool.transitions.manage" label="Cancel Package Change">
                              <button
                                onClick={() => askCancelTransition(t)}
                                className={`${iconBtn} text-red-600 hover:text-red-800`}
                                title="Cancel package change"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            </Gate>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ---------------- REPORT ---------------- */}
          {activeTab === 'report' && (
            <div>
              <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                <h2 className="text-xl font-semibold">Afterschool Report</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-sm text-gray-600">Show / print:</label>
                  <select
                    value={printScope}
                    onChange={(e) => setPrintScope(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="both">Both sections</option>
                    <option value="months">Month by Month only</option>
                    <option value="families">By Family only</option>
                  </select>
                  <button
                    onClick={handleExportCsv}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <FileText className="w-4 h-4" />
                    Export CSV
                  </button>
                  <button
                    onClick={handlePrintReport}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </button>
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                  <div className="text-blue-600 text-sm font-semibold mb-1">Families</div>
                  <div className="text-2xl font-bold text-blue-900">{reportTotals.families}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                  <div className="text-gray-600 text-sm font-semibold mb-1">Charged</div>
                  <div className="text-2xl font-bold text-gray-900">${money(reportTotals.charged)}</div>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg border-2 border-amber-200">
                  <div className="text-amber-600 text-sm font-semibold mb-1">of which Daily</div>
                  <div className="text-2xl font-bold text-amber-900">${money(reportTotals.daily)}</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                  <div className="text-green-600 text-sm font-semibold mb-1">Collected</div>
                  <div className="text-2xl font-bold text-green-900">${money(reportTotals.paid)}</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border-2 border-red-200">
                  <div className="text-red-600 text-sm font-semibold mb-1">Outstanding</div>
                  <div className="text-2xl font-bold text-red-900">${money(reportTotals.balance)}</div>
                </div>
              </div>

              {/* By month */}
              {printScope !== 'families' && (
                <>
                  <h3 className="font-semibold text-gray-800 mb-2">Month by Month</h3>
                  <div className="overflow-x-auto mb-8">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Charges</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Students</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Gross</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Discounts</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Net</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">↳ Monthly</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">↳ Daily</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Collected</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(report.months || []).map((m) => (
                          <tr key={`${m.ChargeYear}-${m.ChargeMonth}`} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap font-medium">
                              {m.MonthName} {m.ChargeYear}
                            </td>
                            <td className="px-3 py-2 text-right">{m.Charges}</td>
                            <td className="px-3 py-2 text-right">{m.Students}</td>
                            <td className="px-3 py-2 text-right">${money(m.Gross)}</td>
                            <td className="px-3 py-2 text-right text-green-600">-${money(m.Discounts)}</td>
                            <td className="px-3 py-2 text-right font-semibold">${money(m.Net)}</td>
                            <td className="px-3 py-2 text-right text-gray-500">${money(m.MonthlyNet)}</td>
                            <td className="px-3 py-2 text-right text-amber-700">${money(m.DailyNet)}</td>
                            <td className="px-3 py-2 text-right text-green-700">${money(m.Collected)}</td>
                            <td className="px-3 py-2 text-right">
                              <span className={Number(m.Outstanding) > 0 ? 'text-red-600 font-semibold' : 'text-gray-500'}>
                                ${money(m.Outstanding)}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {(report.months || []).length === 0 && (
                          <tr>
                            <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                              No charges recorded for {selectedYear}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* By family */}
              {printScope !== 'months' && (
                <>
                  <h3 className="font-semibold text-gray-800 mb-2">By Family</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Family ID</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Family</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Students</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monthly</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Daily</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Charged</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(report.families || []).map((r) => (
                          <tr key={r.FamilyID} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap font-medium">{r.FamilyID}</td>
                            <td className="px-3 py-2">
                              {r.FamilyName || '—'}
                              {/* Enrolled but never charged — otherwise a $0 row
                                  reads as a mistake rather than a to-do. */}
                              {Number(r.Charges) === 0 && Number(r.DailyEnrollments) > 0 && (
                                <span
                                  className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-medium rounded"
                                  title="Daily enrollment with no days logged yet"
                                >
                                  no days logged
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">{r.Students}</td>
                            <td className="px-3 py-2 text-right">${money(r.MonthlyCharged)}</td>
                            <td className="px-3 py-2 text-right text-amber-700">${money(r.DailyCharged)}</td>
                            <td className="px-3 py-2 text-right">${money(r.TotalCharged)}</td>
                            <td className="px-3 py-2 text-right text-green-700">${money(r.TotalPaid)}</td>
                            <td className="px-3 py-2 text-right">
                              <span className={Number(r.Balance) > 0 ? 'text-red-600 font-bold' : 'text-gray-500'}>
                                ${money(r.Balance)}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {(report.families || []).length === 0 && (
                          <tr>
                            <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                              No families with afterschool charges for {selectedYear}
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan="3" className="px-3 py-2 font-bold">TOTAL</td>
                          <td className="px-3 py-2 text-right font-bold">${money(reportTotals.monthly)}</td>
                          <td className="px-3 py-2 text-right font-bold text-amber-700">${money(reportTotals.daily)}</td>
                          <td className="px-3 py-2 text-right font-bold">${money(reportTotals.charged)}</td>
                          <td className="px-3 py-2 text-right font-bold text-green-700">${money(reportTotals.paid)}</td>
                          <td className="px-3 py-2 text-right font-bold text-red-700">${money(reportTotals.balance)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCapSettings && (
        <CapSettingsModal
          apiCall={apiCall}
          selectedYear={selectedYear}
          onClose={() => setShowCapSettings(false)}
          onSaved={loadData}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}

      {showEnrollModal && (
        <AfterschoolEnrollModal
          students={students}
          packages={packages}
          onClose={() => setShowEnrollModal(false)}
          onSuccess={() => {
            setShowEnrollModal(false);
            loadData();
            showSuccess('Afterschool enrollment created');
          }}
          apiCall={apiCall}
          showError={showError}
          selectedYear={selectedYear}
        />
      )}

      {showDayChargesModal && selectedEnrollment && (
        <AfterschoolDayChargesModal
          enrollment={selectedEnrollment}
          apiCall={apiCall}
          onClose={() => { setShowDayChargesModal(false); setSelectedEnrollment(null); }}
          onChanged={loadData}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}

      {showTransitionModal && selectedEnrollment && (
        <AfterschoolTransitionModal
          enrollment={selectedEnrollment}
          packages={packages}
          apiCall={apiCall}
          onClose={() => { setShowTransitionModal(false); setSelectedEnrollment(null); }}
          onSuccess={() => {
            setShowTransitionModal(false);
            setSelectedEnrollment(null);
            loadData();
            showSuccess('Package change scheduled');
          }}
          showError={showError}
        />
      )}

      <ConfirmDialog
        open={!!dialog}
        tone={dialog?.tone}
        title={dialog?.title}
        message={dialog?.message}
        details={dialog?.details}
        confirmLabel={dialog?.confirmLabel}
        cancelLabel={dialog?.cancelLabel}
        requireInput={dialog?.requireInput}
        inputLabel={dialog?.inputLabel}
        inputPlaceholder={dialog?.inputPlaceholder}
        busy={dialogBusy}
        onConfirm={runDialogAction}
        onCancel={closeDialog}
      />
    </div>
  );
};

export default Afterschool;
